import React from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Stack,
  Card,
  CardContent,
} from "@mui/material";
import { Launch } from "@mui/icons-material";
import LoafLockWidget from "../components/LoafLockWidget";

const Lock = () => {
  const API_BASE = process.env.REACT_APP_LOCK_API;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        background: (theme) => theme.palette.background.gradient,
        backgroundAttachment: "fixed",
        py: 8,
        px: { xs: 2, sm: 4, md: 6 },
      }}
    >
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "3rem", md: "4.5rem" },
              mb: 4,
              lineHeight: 1.1,
              background: "linear-gradient(135deg, #ffffff 0%, #79d1c3 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Lock
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.5rem", md: "2.5rem" },
              mb: 4,
              color: "rgba(255, 255, 255, 0.8)",
              fontWeight: 300,
            }}
          >
            package-lock.json for prompts
          </Typography>

          {/* Feature Bullets */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={4}
            sx={{
              justifyContent: "center",
              mb: 6,
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#79d1c3",
                  mb: 1,
                  fontWeight: 600,
                }}
              >
                Reproducible
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  lineHeight: 1.6,
                }}
              >
                Lock your AI prompts and responses for consistent, reproducible
                results across environments.
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#79d1c3",
                  mb: 1,
                  fontWeight: 600,
                }}
              >
                Drift Defense
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  lineHeight: 1.6,
                }}
              >
                Detect when your prompts drift from their locked versions and
                maintain prompt integrity.
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#79d1c3",
                  mb: 1,
                  fontWeight: 600,
                }}
              >
                CI-Friendly
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255, 255, 255, 0.7)",
                  lineHeight: 1.6,
                }}
              >
                Integrate with your CI/CD pipelines for automated prompt
                validation and testing.
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Demo Widget */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h4"
            sx={{
              textAlign: "center",
              mb: 4,
              color: "#ffffff",
              fontWeight: 500,
            }}
          >
            Try It Live
          </Typography>
          <LoafLockWidget />
        </Box>

        {/* API Console Button */}
        {API_BASE && (
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Button
              variant="outlined"
              startIcon={<Launch />}
              component="a"
              href={`${API_BASE}/docs`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: "1.1rem",
                borderColor: "rgba(255, 255, 255, 0.2)",
                color: "#ffffff",
                "&:hover": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  backgroundColor: "rgba(255, 255, 255, 0.02)",
                  transform: "scale(1.02)",
                },
              }}
            >
              Open API Console
            </Button>
          </Box>
        )}

        {/* CLI Usage */}
        <Box sx={{ maxWidth: "600px", mx: "auto" }}>
          <Typography
            variant="h5"
            sx={{
              textAlign: "center",
              mb: 3,
              color: "#ffffff",
              fontWeight: 500,
            }}
          >
            CLI Usage
          </Typography>
          <Card
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.04)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 4,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255, 255, 255, 0.6)",
                  mb: 2,
                  textAlign: "center",
                }}
              >
                Install and use the Lock CLI:
              </Typography>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                  borderRadius: 2,
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    color: "#79d1c3",
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                    margin: 0,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {`# Freeze current prompts to lockfile
python -m backend.cli.loaf freeze

# Check for prompt drift
python -m backend.cli.loaf diff

# Validate against lockfile
python -m backend.cli.loaf validate`}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default Lock;
